/**
 * AP-11-E (E4) — Speicher des eigenen Mandanten: belegt, Kontingent, Aufteilung.
 *
 * Liegt in `lib/`, weil der Hinweis im Rahmen der Anwendung und die Seite
 * „Speicher" denselben Stand brauchen. Ob ein Upload gesperrt ist, entscheidet
 * der Server; das Frontend zeigt es nur.
 */
import { create } from 'zustand'
import { api } from '@/lib/axios'

export type SpeicherStufe = 'OK' | 'WARN' | 'FULL'

export interface SpeicherBereich {
  /** Modul-Kenner der Datei (`PROJECT`, `SITE_REPORT`, `BRANDING` …). */
  module: string
  files: number
  bytes: number
  /** false = zählt nicht ins Kontingent (Logo, Briefpapier). */
  countsToQuota: boolean
}

export interface SpeicherUebersicht {
  usedBytes: number
  limitBytes: number | null
  /** Anteil 0..1; null ohne Grenze. */
  ratio: number | null
  level: SpeicherStufe
  /**
   * `CUSTOM` = vom Betreiber hinterlegt, `PLAN` = Startwert, `NONE` = ohne Grenze,
   * `OWN` = eigener Speicher des Betriebs — dort gilt kein Kontingent.
   */
  limitSource: 'CUSTOM' | 'PLAN' | 'NONE' | 'OWN'
  planLimitBytes: number | null
  areas: SpeicherBereich[]
  storageReady: boolean
}

const KB = 1024
const MB = 1024 * KB
const GB = 1024 * MB

/** „17,4 GB", „512 MB", „12 KB" — deutsch geschrieben, eine Nachkommastelle nur, wo sie etwas sagt. */
export function speicherText(bytes: number): string {
  const zahl = (n: number) => new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(n)
  if (bytes >= GB) return `${zahl(bytes / GB)} GB`
  if (bytes >= MB) return `${zahl(bytes / MB)} MB`
  if (bytes <= 0) return '0 KB'
  return `${zahl(Math.max(1, Math.round(bytes / KB)))} KB`
}

/** Anteil in ganzen Prozent, gedeckelt auf 100 — für den Balken. */
export function speicherProzent(u: Pick<SpeicherUebersicht, 'ratio'>): number {
  return u.ratio === null ? 0 : Math.min(100, Math.round(u.ratio * 100))
}

export async function ladeSpeicher(): Promise<SpeicherUebersicht> {
  const res = await api.get<{ data: SpeicherUebersicht }>('/storage/usage')
  return res.data.data
}

interface SpeicherZustand {
  stand: SpeicherUebersicht | null
  lade: () => Promise<SpeicherUebersicht | null>
  setze: (stand: SpeicherUebersicht) => void
  leere: () => void
}

/** Der Stand einmal je Anmeldung für den Hinweis; die Seite lädt ihn beim Öffnen neu. */
export const useSpeicher = create<SpeicherZustand>((set) => ({
  stand: null,
  lade: async () => {
    try {
      const stand = await ladeSpeicher()
      set({ stand })
      return stand
    } catch {
      // Der Hinweis ist eine Bequemlichkeit — sein Fehlen darf die Anwendung nicht stören.
      return null
    }
  },
  setze: (stand) => set({ stand }),
  leere: () => set({ stand: null }),
}))

// ─── Eigener Speicher (AP-11-E Phase E5) ──────────────────────────────────────

/** Was der Administrator einträgt. Die Schlüssel gehen zum Server und kommen nie zurück. */
export interface EigenerSpeicherEingabe {
  /** Leer = Amazon S3. */
  endpoint: string | null
  bucket: string
  region: string | null
  accessKey: string
  secretKey: string
}

export interface SpeicherUmzug {
  id: string
  status: 'RUNNING' | 'FAILED' | 'DONE'
  direction: 'TO_OWN' | 'TO_CENTRAL' | 'OWN_TO_OWN'
  totalObjects: number
  movedObjects: number
  totalBytes: number
  movedBytes: number
  lastError: string | null
  startedAt: string
  finishedAt: string | null
}

/** Stand des eigenen Speichers — ohne Zugangsdaten, nur das Ende des Zugriffsschlüssels. */
export interface EigenerSpeicher {
  /** Ist eigener Speicher auf diesem System eingerichtet? */
  available: boolean
  provider: 'central' | 's3'
  endpoint: string | null
  bucket: string | null
  region: string | null
  accessKeySuffix: string | null
  connectedAt: string | null
  /** Hat Symphenia den Rückweg freigegeben? */
  returnApproved: boolean
  lastFailureAt: string | null
  lastFailureMessage: string | null
  move: SpeicherUmzug | null
}

type Antwort<T> = { data: T }

export const eigenerSpeicherApi = {
  stand: async (): Promise<EigenerSpeicher> => (await api.get<Antwort<EigenerSpeicher>>('/storage/own')).data.data,
  teste: async (eingabe: EigenerSpeicherEingabe): Promise<void> => {
    await api.post('/storage/own/test', eingabe)
  },
  verbinde: async (eingabe: EigenerSpeicherEingabe): Promise<EigenerSpeicher> =>
    (await api.post<Antwort<EigenerSpeicher>>('/storage/own/connect', eingabe)).data.data,
  fortsetzen: async (): Promise<EigenerSpeicher> =>
    (await api.post<Antwort<EigenerSpeicher>>('/storage/own/move/retry')).data.data,
  zurueck: async (): Promise<EigenerSpeicher> => (await api.post<Antwort<EigenerSpeicher>>('/storage/own/return')).data.data,
}

/** Fortschritt eines Umzugs in ganzen Prozent — nach Dateien, nicht nach Bytes. */
export function umzugProzent(u: Pick<SpeicherUmzug, 'totalObjects' | 'movedObjects' | 'status'>): number {
  if (u.status === 'DONE') return 100
  if (u.totalObjects <= 0) return 0
  return Math.min(100, Math.floor((u.movedObjects / u.totalObjects) * 100))
}
